import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navigation from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";

const EFFECTIVE_DATE = "December 11, 2025";
const COMPANY = "Voxinity AI";
const CONTACT_EMAIL = "support@voxinity.ai";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10" data-testid={`privacy-section-${id}`}>
    <h2 className="font-display font-semibold text-2xl md:text-3xl text-white mb-4">
      {title}
    </h2>
    <div className="space-y-4 text-gray-300 leading-relaxed text-base">
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="privacy-policy-page"
    >
      <Navigation />

      <main className="relative pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors mb-8"
            data-testid="privacy-back-link"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>

          <header className="mb-12 border-b border-white/10 pb-8">
            <h1 className="font-display font-semibold text-4xl sm:text-5xl text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-400 text-sm">
              Effective Date: <span className="text-gray-200">{EFFECTIVE_DATE}</span>
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Last Updated: <span className="text-gray-200">{EFFECTIVE_DATE}</span>
            </p>
          </header>

          <div className="bg-teal-400/5 border border-teal-400/20 rounded-xl p-5 mb-12">
            <p className="text-sm text-gray-300 leading-relaxed">
              This Privacy Policy describes how {COMPANY} ("we", "us", or "our")
              collects, uses, discloses, and safeguards information when you use
              our AI voice and chat agent platform (the "Service"). By using the
              Service, you agree to the practices described in this Policy.
            </p>
          </div>

          <Section id="information-we-collect" title="1. Information We Collect">
            <p>
              We collect information you provide directly to us, information
              collected automatically through your use of the Service, and
              information from third-party integrations you authorize.
            </p>
            <h3 className="text-lg font-medium text-white mt-4">
              a. Account & Profile Information
            </h3>
            <p>
              Name, email address, phone number, business name, billing address,
              and password (stored as a salted, one-way hash).
            </p>
            <h3 className="text-lg font-medium text-white mt-4">
              b. Customer & Contact Data
            </h3>
            <p>
              When you connect a CRM or import contacts, we process the contact
              records you provide, including names, phone numbers, email
              addresses, custom fields, and tags. You are the controller of this
              data; we act as a processor on your behalf.
            </p>
            <h3 className="text-lg font-medium text-white mt-4">
              c. Voice, Call & Conversation Data
            </h3>
            <p>
              We record, transcribe, and store voice calls, SMS messages, chat
              widget conversations, and related metadata (timestamps, call
              duration, outcome, sentiment) to deliver, improve, and audit the
              Service. Call recordings may be retained for up to 90 days unless
              you configure a different retention period or request earlier
              deletion.
            </p>
            <h3 className="text-lg font-medium text-white mt-4">
              d. Payment Information
            </h3>
            <p>
              Payment card details are collected and processed by our PCI-DSS
              compliant payment processor (Stripe). We do not store full card
              numbers on our systems.
            </p>
            <h3 className="text-lg font-medium text-white mt-4">
              e. Technical & Usage Data
            </h3>
            <p>
              IP address, browser type, device identifiers, referring URLs,
              pages viewed, feature usage, and diagnostic logs.
            </p>
          </Section>

          <Section id="how-we-use" title="2. How We Use Information">
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, operate, maintain, and improve the Service.</li>
              <li>
                To process voice calls, SMS, and chat conversations through
                connected AI models and telephony providers.
              </li>
              <li>
                To sync activity (calls, recordings, transcripts, contact
                updates, notes, tags) back to CRMs you have authorized.
              </li>
              <li>To bill, invoice, and process payments.</li>
              <li>
                To send service-related notifications, security alerts, and
                administrative messages.
              </li>
              <li>
                To detect, investigate, and prevent fraud, abuse, and security
                incidents.
              </li>
              <li>To comply with legal obligations and enforce our Terms.</li>
            </ul>
            <p className="mt-4">
              We do <span className="text-white font-medium">not</span> sell
              personal information, and we do not use customer voice or
              conversation content to train third-party foundation models
              without your explicit consent.
            </p>
          </Section>

          <Section id="sharing" title="3. How We Share Information">
            <p>
              We share information only with the categories of recipients
              described below, and only as required to deliver the Service:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="text-white font-medium">
                  Sub-Processors & Service Providers:
                </span>{" "}
                We engage trusted third parties to host infrastructure, send
                emails, process payments, deliver telephony, run speech-to-text
                and text-to-speech, and provide large language model inference.
                These vendors are contractually bound to use information only
                to perform services for us.
              </li>
              <li>
                <span className="text-white font-medium">CRM Integrations:</span>{" "}
                When you authorize a CRM connection (e.g., via OAuth), we send
                and receive data with that CRM strictly under your direction.
              </li>
              <li>
                <span className="text-white font-medium">Legal & Safety:</span>{" "}
                When required by law, subpoena, court order, or to protect the
                rights, property, or safety of {COMPANY}, our users, or others.
              </li>
              <li>
                <span className="text-white font-medium">Business Transfers:</span>{" "}
                In the event of a merger, acquisition, financing, or sale of
                assets, information may transfer subject to this Policy.
              </li>
            </ul>
          </Section>

          <Section id="sub-processors" title="4. Sub-Processors">
            <p>
              We rely on the following categories of sub-processors. By using
              the Service, you authorize their use:
            </p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      Vendor
                    </th>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      Purpose
                    </th>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      Data Handled
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Twilio</td>
                    <td className="py-3 px-4">Telephony, SMS, call routing</td>
                    <td className="py-3 px-4">Phone numbers, call audio, SMS content</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">ElevenLabs</td>
                    <td className="py-3 px-4">Text-to-speech voice synthesis</td>
                    <td className="py-3 px-4">Outbound text to be voiced</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">OpenAI / Anthropic / Google</td>
                    <td className="py-3 px-4">Large language model inference</td>
                    <td className="py-3 px-4">Conversation messages, system prompts</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Stripe</td>
                    <td className="py-3 px-4">Payment processing</td>
                    <td className="py-3 px-4">Billing details, payment methods</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">CRM Platforms (via OAuth)</td>
                    <td className="py-3 px-4">Contact sync, calendar, opportunities</td>
                    <td className="py-3 px-4">Contacts, notes, calls, appointments</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Cloud Infrastructure</td>
                    <td className="py-3 px-4">Hosting & storage</td>
                    <td className="py-3 px-4">All Service data at rest</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="ghl-data-usage" title="5. CRM Data Usage (LeadConnector)">
            <p>
              When you connect a LeadConnector sub-account or agency to
              Voxinity AI through the LeadConnector Marketplace OAuth flow, we read from
              and write to a clearly scoped set of resources in your LeadConnector
              account to operate the Service on your behalf. The table below
              lists every category of data we touch, what we do with it, and
              how long we retain it.
            </p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      LeadConnector Resource
                    </th>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      Read
                    </th>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      Write
                    </th>
                    <th className="text-left py-3 px-4 text-white font-medium">
                      Retention
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Contacts</td>
                    <td className="py-3 px-4">Lookup by phone / email, fetch custom fields and tags for call context.</td>
                    <td className="py-3 px-4">Create new contacts, update name/phone/email, append notes, add or remove tags, write structured call summaries to custom fields.</td>
                    <td className="py-3 px-4">Mirror retained 13 months</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Conversations & Messages</td>
                    <td className="py-3 px-4">Read inbound SMS / chat history to maintain conversational context.</td>
                    <td className="py-3 px-4">Send outbound SMS replies, post-call summaries, and follow-up messages via your LeadConnector number.</td>
                    <td className="py-3 px-4">13 months</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Calendars & Appointments</td>
                    <td className="py-3 px-4">Read calendar IDs and free / busy windows when the AI offers slots.</td>
                    <td className="py-3 px-4">Create, reschedule, or cancel appointments only when a caller confirms.</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Opportunities</td>
                    <td className="py-3 px-4">Read opportunity stage to brief the AI during calls.</td>
                    <td className="py-3 px-4">Move opportunities between stages when a workflow asks for it.</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Custom Fields & Custom Values</td>
                    <td className="py-3 px-4">Discover schema so the AI knows which fields exist.</td>
                    <td className="py-3 px-4">Populate fields with call outcome, summary, transcript URL, last-call timestamp.</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Tags</td>
                    <td className="py-3 px-4">List available tags.</td>
                    <td className="py-3 px-4">Apply tags such as "AI-Called", "Booked", "No-Answer".</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Workflows</td>
                    <td className="py-3 px-4">List active workflows so customers can target them.</td>
                    <td className="py-3 px-4">Trigger a specific workflow on demand from a Voxinity action.</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Locations & Users</td>
                    <td className="py-3 px-4">Read location metadata and assigned users for display in our dashboard.</td>
                    <td className="py-3 px-4">None.</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">Forms</td>
                    <td className="py-3 px-4">List form submissions so the AI can reference them.</td>
                    <td className="py-3 px-4">None.</td>
                    <td className="py-3 px-4">For the life of the connection</td>
                  </tr>
                  <tr className="border-t border-white/10">
                    <td className="py-3 px-4">OAuth Tokens</td>
                    <td className="py-3 px-4">Stored encrypted at rest, refreshed automatically before expiry.</td>
                    <td className="py-3 px-4">Refresh tokens are rotated by the LeadConnector OAuth server.</td>
                    <td className="py-3 px-4">Deleted on uninstall or disconnect</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              We do <span className="text-white font-medium">not</span> sell,
              rent, or transfer LeadConnector data to third parties, and we do
              <span className="text-white font-medium"> not </span>
              use the content of LeadConnector conversations, contacts, or calls to
              train any third-party machine-learning model. Conversation
              content is sent to large language model providers
              (OpenAI / Anthropic / Google) only to generate the live response
              and is excluded from their training programs under our
              zero-data-retention enterprise agreements.
            </p>
            <p>
              When you uninstall the Voxinity app from a LeadConnector
              sub-account, our LeadConnector Marketplace webhook handler revokes the
              stored OAuth tokens, halts every background sync loop for that
              sub-account, and flips the integration card in Voxinity to
              "Disconnected" within seconds. You can independently delete a
              workspace and all of its CRM-derived data at any time by
              emailing{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-teal-400 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="retention" title="6. Data Retention">
            <p>
              We retain personal information for as long as your account is
              active or as needed to provide the Service. Default retention
              windows:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account & billing records: duration of account + 7 years.</li>
              <li>Call recordings: up to 90 days (configurable per workspace).</li>
              <li>Transcripts & conversation logs: up to 13 months.</li>
              <li>Diagnostic logs: up to 30 days.</li>
            </ul>
            <p>
              You may request deletion of your account and associated data at
              any time by emailing{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-teal-400 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              . We will action verified deletion requests within 30 days,
              subject to legal retention obligations.
            </p>
          </Section>

          <Section id="security" title="7. Security">
            <p>
              We implement administrative, technical, and physical safeguards
              designed to protect the information we process. These include
              encryption in transit (TLS 1.2+), encryption at rest, role-based
              access controls, least-privilege provisioning, audit logging, and
              periodic security reviews. No method of transmission or storage
              is 100% secure; we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="your-rights" title="8. Your Rights">
            <p>
              Depending on your jurisdiction, you may have the right to access,
              correct, port, restrict, or delete personal information we hold
              about you, and to object to or withdraw consent for certain
              processing. To exercise these rights, contact us at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-teal-400 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              . We will respond within the timeframe required by applicable
              law.
            </p>
            <p>
              <span className="text-white font-medium">California Residents (CCPA/CPRA):</span>{" "}
              You have the right to know what personal information we collect,
              the right to delete it, the right to correct inaccurate
              information, the right to opt out of sale or sharing (we do
              neither), and the right not to be discriminated against for
              exercising your rights.
            </p>
            <p>
              <span className="text-white font-medium">EU/UK Residents (GDPR/UK GDPR):</span>{" "}
              We process personal data on the legal bases of contract
              performance, legitimate interests, consent, and legal obligation.
              You may lodge a complaint with your local data protection
              authority.
            </p>
          </Section>

          <Section id="recordings-consent" title="9. Call Recording & Consent">
            <p>
              You are responsible for ensuring that all parties to a call are
              notified of, and where required by law, consent to call
              recording. The Service provides automated disclosure features;
              you must enable and configure them in accordance with the laws
              of every jurisdiction in which your calls originate or
              terminate.
            </p>
          </Section>

          <Section id="children" title="10. Children's Privacy">
            <p>
              The Service is not directed to individuals under 16. We do not
              knowingly collect personal information from children. If you
              believe a child has provided us with personal information,
              contact us and we will delete it.
            </p>
          </Section>

          <Section id="international" title="11. International Data Transfers">
            <p>
              We may process and store information in the United States and
              other countries that may have different data protection laws
              than your country of residence. Where required, we rely on
              appropriate safeguards such as Standard Contractual Clauses.
            </p>
          </Section>

          <Section id="cookies" title="12. Cookies & Tracking">
            <p>
              We use strictly necessary cookies for authentication and session
              management, and limited analytics cookies to understand how the
              Service is used. You may control cookies through your browser
              settings. Disabling necessary cookies may impair Service
              functionality.
            </p>
          </Section>

          <Section id="changes" title="13. Changes to This Policy">
            <p>
              We may update this Policy from time to time. Material changes
              will be communicated by email or via an in-product notice at
              least 30 days before they take effect. Continued use of the
              Service after the effective date constitutes acceptance of the
              updated Policy.
            </p>
          </Section>

          <Section id="contact" title="14. Contact Us">
            <p>
              For privacy questions, requests, or complaints, contact our
              Privacy team at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-teal-400 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <div className="mt-16 p-5 rounded-xl border border-amber-400/20 bg-amber-400/5 text-sm text-amber-100/80">
            <p className="font-medium text-amber-200 mb-1">
              Legal Disclaimer
            </p>
            <p>
              This document is provided for general informational purposes
              and does not constitute legal advice. {COMPANY} recommends that
              you consult qualified legal counsel to confirm that this Policy
              meets the regulatory requirements applicable to your business
              and the jurisdictions in which you operate.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
